import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  Button,
  Flex,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowRight,
  IconCheck,
  IconHistory,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useElementSize } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { OpioidConversionVersionApi } from '../../../global/apis/OpioidConversionVersionApi/OpioidConversionVersionApi';
import { IOpioidConversionVersion } from '../../../global/entities/MME/MME';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';

const OpioidConversionVersionsTableInner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ref } = useElementSize();
  const {
    setSorting,
    sortBy,
    reverseSortDirection,
    sortedData,
    setSortedData,
  } = useTable();

  const [data, setData] = useState<IOpioidConversionVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteStatus, setDeleteStatus] = useState<Record<number, { canDelete: boolean; isActive: boolean; isUsedInCalculations: boolean }>>({});

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const versions = await OpioidConversionVersionApi.getAll();
      setData(versions);

      // Check delete eligibility for each version
      const deleteStatusMap: Record<number, { canDelete: boolean; isActive: boolean; isUsedInCalculations: boolean }> = {};
      await Promise.all(
        versions.map(async (version) => {
          try {
            const status = await OpioidConversionVersionApi.canDeleteVersion(version.id);
            deleteStatusMap[version.id] = {
              canDelete: status.canDelete,
              isActive: status.isActive,
              isUsedInCalculations: status.isUsedInCalculations,
            };
          } catch (error) {
            console.error(`Error checking delete status for version ${version.id}:`, error);
            deleteStatusMap[version.id] = {
              canDelete: false,
              isActive: false,
              isUsedInCalculations: false,
            };
          }
        })
      );
      setDeleteStatus(deleteStatusMap);
    } catch (error) {
      console.error('Error loading conversion versions:', error);
      notifications.show({
        title: t('opioidConversionVersionsTable.error'),
        message: t('opioidConversionVersionsTable.loadErrorMessage'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let filtered = data;

    // Filter by search term
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.versionName.toLowerCase().includes(searchLower) ||
          (v.description && v.description.toLowerCase().includes(searchLower)),
      );
    }

    setSortedData(filtered);
  }, [data, search, setSortedData]);

  const handleActivateVersion = (version: IOpioidConversionVersion) => {
    modals.openConfirmModal({
      title: t('opioidConversionVersionsTable.activateVersionTitle'),
      children: (
        <Stack gap={'sm'}>
          <Text size="sm">
            {t('opioidConversionVersionsTable.activateVersionMessage', { versionName: version.versionName })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('opioidConversionVersionsTable.activateVersionNote')}
          </Text>
        </Stack>
      ),
      labels: { confirm: t('opioidConversionVersionsTable.activate'), cancel: t('button.cancel') },
      confirmProps: { color: 'blue' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: async () => {
        try {
          await OpioidConversionVersionApi.activateVersion(version.id);
          notifications.show({
            title: t('opioidConversionVersionsTable.success'),
            message: t('opioidConversionVersionsTable.activateSuccessMessage', { versionName: version.versionName }),
            color: 'green',
          });
          loadData();
        } catch (error) {
          console.error('Error activating version:', error);
          notifications.show({
            title: t('opioidConversionVersionsTable.error'),
            message: t('opioidConversionVersionsTable.activateErrorMessage'),
            color: 'red',
          });
        }
      },
      size: 'lg',
      radius: 'xs',
    });
  };

  const handleDeleteVersion = (version: IOpioidConversionVersion) => {
    modals.openConfirmModal({
      title: t('opioidConversionVersionsTable.deleteVersionTitle'),
      children: (
        <Stack gap={'sm'}>
          <Text size="sm">
            {t('opioidConversionVersionsTable.deleteVersionMessage', { versionName: version.versionName })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('opioidConversionVersionsTable.deleteVersionNote')}
          </Text>
        </Stack>
      ),
      labels: { confirm: t('opioidConversionVersionsTable.delete'), cancel: t('button.cancel') },
      confirmProps: { color: 'red' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: async () => {
        try {
          await OpioidConversionVersionApi.deleteVersion(version.id);
          notifications.show({
            title: t('opioidConversionVersionsTable.success'),
            message: t('opioidConversionVersionsTable.deleteSuccessMessage', { versionName: version.versionName }),
            color: 'green',
          });
          loadData();
        } catch (error) {
          console.error('Error deleting version:', error);
          notifications.show({
            title: t('opioidConversionVersionsTable.error'),
            message: t('opioidConversionVersionsTable.deleteErrorMessage'),
            color: 'red',
          });
        }
      },
      size: 'lg',
      radius: 'xs',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) 
    return <Loading visible={true} loadingMessage={t('opioidConversionVersionsTable.loading')} />;

  const rows = sortedData.map((version: IOpioidConversionVersion) => (
    <Table.Tr key={version.id}>
      <Table.Td>
        <Text size="sm" fw={500}>{version.versionName}</Text>
      </Table.Td>
      <Table.Td style={{ maxWidth: '400px' }}>
        <Text size="sm" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {version.description || '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        {formatDate(version.createdDate)}
      </Table.Td>
      <Table.Td>
        <Text size="sm" c={version.isActive ? 'green' : 'gray'}>
          {version.isActive ? t('opioidConversionVersionsTable.active') : t('opioidConversionVersionsTable.inactive')}
        </Text>
      </Table.Td>
      <Table.Td>
        <Flex gap="xs">
          <Tooltip label={t('opioidConversionVersionsTable.viewDetails')}>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="blue"
              onClick={() =>
                navigate(`/adminDashboard/conversion-versions/${version.id}`)
              }
            >
              <IconArrowRight size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={
            version.isActive 
              ? t('opioidConversionVersionsTable.alreadyActive')
              : t('opioidConversionVersionsTable.activateThisVersion')
          }>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="green"
              onClick={() => handleActivateVersion(version)}
              disabled={version.isActive}
            >
              <IconCheck size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={
            deleteStatus[version.id]?.canDelete 
              ? t('opioidConversionVersionsTable.deleteThisVersion')
              : deleteStatus[version.id]?.isActive
                ? t('opioidConversionVersionsTable.cannotDeleteActiveVersion')
                : deleteStatus[version.id]?.isUsedInCalculations
                  ? t('opioidConversionVersionsTable.cannotDeleteUsedInCalculations')
                  : t('opioidConversionVersionsTable.cannotDelete')
          }>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={() => handleDeleteVersion(version)}
              disabled={!deleteStatus[version.id]?.canDelete}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Flex mb={'xl'} mt={'xl'} align={'center'} justify={'space-between'}>
        <Button
          onClick={() => navigate('/adminDashboard/conversion-versions')}
          variant="transparent"
        >
          <Title c="black" order={4}>
            <IconHistory size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {t('opioidConversionVersionsTable.title')}
          </Title>
        </Button>
      </Flex>

      <Paper ref={ref} w={'100%'}>
        <Stack gap={'md'}>
          <Flex gap={'md'} align={'center'}>
            <TextInput
              placeholder={t('opioidConversionVersionsTable.searchPlaceholder')}
              rightSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
          </Flex>

          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Th
                    sorted={sortBy === 'versionName'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('versionName', sortedData)}
                  >
                    {t('opioidConversionVersionsTable.versionName')}
                  </Th>
                  <Th
                    sorted={sortBy === 'description'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('description', sortedData)}
                  >
                    {t('opioidConversionVersionsTable.description')}
                  </Th>
                  <Th
                    sorted={sortBy === 'createdDate'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('createdDate', sortedData)}
                  >
                    {t('opioidConversionVersionsTable.createdDate')}
                  </Th>
                  <Th
                    sorted={sortBy === 'isActive'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('isActive', sortedData)}
                  >
                    {t('opioidConversionVersionsTable.status')}
                  </Th>
                  <Table.Th>{t('opioidConversionVersionsTable.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </ScrollArea>

          {sortedData.length === 0 && (
            <Text ta="center" c="dimmed" py={'xl'}>
              {t('opioidConversionVersionsTable.noVersionsFound')}
            </Text>
          )}
        </Stack>
      </Paper>
    </>
  );
};

export const OpioidConversionVersionsTable: React.FC = () => {
  return (
    <TableContextProvider>
      <OpioidConversionVersionsTableInner />
    </TableContextProvider>
  );
};
